import { Text } from '@mantine/core';
import { useOidcContext } from '@uag/react-core';

import { isLoginEnabled } from '../../authMode';
import { useCatalogLabels } from '../labels';

// Whenever login is disabled (see authMode.ts - by default that's the static catalog export,
// which skips AuthProvider entirely), useOidcContext() would throw without an ancestor
// AuthProvider. UserGreetingContent (the only place that calls the hook) must therefore never be
// MOUNTED in that case - not just conditionally rendered inside it.

interface OidcUser {
    name?: string;
    given_name?: string;
    family_name?: string;
    preferred_username?: string;
}

// Best-effort claim lookup, in order of how likely each is to hold a human-readable display name
// on a Microsoft Entra ID / OIDC token - this sandbox has no access to the private
// @uag/react-core package to confirm useOidcContext()'s exact `user` shape, so this hasn't been
// verified against a real login. If the greeting doesn't show (or shows the wrong thing), check
// what claims the real token actually carries and adjust here.
// `.trim()` on the result: the rendered greeting showed as "Herzlich willkommen Markus Gerber !",
// i.e. a space before the exclamation mark, although the JSX has none - so the claim itself carries
// surrounding whitespace. Trimming here rather than in the JSX keeps every caller of this helper
// clean.
const displayName = (user: OidcUser | undefined) =>
    (
        user?.name ||
        [user?.given_name, user?.family_name].filter(Boolean).join(' ') ||
        user?.preferred_username ||
        ''
    ).trim() || undefined;

const UserGreetingContent = () => {
    const { L } = useCatalogLabels();
    const { user } = useOidcContext();
    const name = displayName(user);

    if (!name) {
        return null;
    }
    return (
        <Text c="dimmed" size="sm">
            {L.welcomeGreeting} {name}!
        </Text>
    );
};

/** "Herzlich willkommen <Name>!" above the search mask heading - only ever mounted when a real
 * OIDC session can exist (see isLoginEnabled above). */
export const UserGreeting = () => (isLoginEnabled ? <UserGreetingContent /> : null);
